import {
    Box,
    Checkbox,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    TableCell,
    TableRow,
    TextField,
    Typography,
    styled,
} from '@mui/material';
import { Pencil } from 'lucide-react';
import { useRef, useState } from 'react';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import { DicomRoi } from '~/domain/dicom/dicomRoi';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { RoiFieldEdits } from '~/hooks/export/useExportEditing';

const EDITABLE_ROI_TYPES = [
    RoiInterpretedType.ORGAN,
    RoiInterpretedType.GTV,
    RoiInterpretedType.CTV,
    RoiInterpretedType.PTV,
    RoiInterpretedType.CONTROL,
    RoiInterpretedType.SUPPORT,
    RoiInterpretedType.DOSE_REGION,
];

type StructureRowProps = {
    structure: DicomRoi;
    onSelect: (structure: DicomRoi) => void;
    selectedStructures: DicomRoi[];
    ssUid: string;
    effectiveIsApproved: boolean;
    ssApprovalEdit?: DICOMApprovalStatus;
    roiEdits: RoiFieldEdits;
    setRoiName: (name: string) => void;
    setRoiType: (type: RoiInterpretedType) => void;
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        cursor: 'pointer',
    },
    '&:hover .hover-add-dose': {
        display: 'inline-flex',
    },
    '& .roi-edit-btn': {
        visibility: 'hidden',
    },
    '&:hover .roi-edit-btn': {
        visibility: 'visible',
    },
}));

const StructureRow = ({
    structure,
    selectedStructures,
    onSelect,
    effectiveIsApproved,
    ssApprovalEdit,
    roiEdits,
    setRoiName,
    setRoiType,
}: StructureRowProps) => {
    // effectiveIsApproved already encodes all blocking conditions
    // (not AI-generated, or SS-level approved) — no per-ROI check needed.
    const isEditable = !effectiveIsApproved;
    const effectiveName = roiEdits.name ?? structure.name;
    const effectiveType = roiEdits.interpretedType ?? structure.interpretedType;
    // Mirrors commitAllEdits cascade logic:
    // - Renamed ROIs are always UNAPPROVED (modified after review).
    // - When the SS approval is changed, the new status cascades to all
    //   non-renamed ROIs so the table reflects the pending export state.
    const effectiveApprovalStatus: DICOMApprovalStatus = (() => {
        if (roiEdits.name !== undefined) {
            return DICOMApprovalStatus.UNAPPROVED;
        }
        if (ssApprovalEdit !== undefined) {
            return ssApprovalEdit;
        }
        return structure.approvalStatus;
    })();
    const [editing, setEditing] = useState(false);
    const [typeSelectOpen, setTypeSelectOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Include current type in the options list even if it's not one of the standard editable types
    const typeOptions = EDITABLE_ROI_TYPES.includes(effectiveType)
        ? EDITABLE_ROI_TYPES
        : [effectiveType, ...EDITABLE_ROI_TYPES];

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setEditing(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleBlur = () => {
        setEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            setEditing(false);
        }
    };

    const handleTypeEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setTypeSelectOpen(true);
    };

    const handleTypeChange = (e: SelectChangeEvent<string>) => {
        setRoiType(e.target.value as RoiInterpretedType);
    };

    return (
        <StyledTableRow onClick={() => onSelect(structure)}>
            <TableCell
                align="left"
                sx={{
                    paddingLeft: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                }}
            >
                <Checkbox
                    size="small"
                    checked={selectedStructures.includes(structure)}
                />
                {editing ? (
                    <TextField
                        inputRef={inputRef}
                        variant="standard"
                        size="small"
                        value={effectiveName}
                        onChange={(e) => setRoiName(e.target.value)}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{ style: { fontSize: 13 } }}
                        sx={{ minWidth: 80 }}
                    />
                ) : (
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        minWidth={80}
                    >
                        <Typography fontSize={13}>{effectiveName}</Typography>
                        {isEditable && (
                            <IconButton
                                className="roi-edit-btn"
                                size="small"
                                onClick={handleEditClick}
                                aria-label="Edit name"
                                sx={{ p: 0.25 }}
                            >
                                <Pencil size={12} />
                            </IconButton>
                        )}
                    </Box>
                )}
            </TableCell>
            <TableCell align="left" onClick={(e) => e.stopPropagation()}>
                {typeSelectOpen ? (
                    <Select
                        variant="standard"
                        size="small"
                        value={effectiveType}
                        open={typeSelectOpen}
                        onOpen={() => setTypeSelectOpen(true)}
                        onClose={() => setTypeSelectOpen(false)}
                        onChange={handleTypeChange}
                        sx={{ fontSize: 13, minWidth: 80 }}
                    >
                        {typeOptions.map((t) => (
                            <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
                                {t || '—'}
                            </MenuItem>
                        ))}
                    </Select>
                ) : (
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography fontSize={13}>
                            {effectiveType || '—'}
                        </Typography>
                        {isEditable && (
                            <IconButton
                                className="roi-edit-btn"
                                size="small"
                                onClick={handleTypeEditClick}
                                aria-label="Edit type"
                                sx={{ p: 0.25 }}
                            >
                                <Pencil size={12} />
                            </IconButton>
                        )}
                    </Box>
                )}
            </TableCell>
            <TableCell align="right">
                <ReviewStatus
                    status={effectiveApprovalStatus}
                    boxProps={{ justifyContent: 'end' }}
                    fontSize={12}
                />
            </TableCell>
        </StyledTableRow>
    );
};

export default StructureRow;
